import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { ActionStatus } from '@ngxs/store/src/actions-stream';
import _ from 'lodash';
import { filter } from 'rxjs/operators';
import { LongRunningContextCategoryTyp } from '../../../samwebDescription/samweb/waitingContext/waitingContextCategory';
import { LongRunningContextStarted, LongRunningContextStopped } from './running-actions.actions';

export interface LongRunningContextBrickContainer {
   brickInstanceId: string;
   waitingContexts: { [waitingContextKey: string]: number };
}

export interface LongRunningContextStateModel {
   brickWaitingContexts: { [brickInstanceId: string]: LongRunningContextBrickContainer };
}

@State<LongRunningContextStateModel>({
   name: 'longRunningContext',
   defaults: {
      brickWaitingContexts: {},
   },
})
@Injectable()
export class LongRunningContextState {
   constructor(private actions$: Actions, private store: Store) {
      this.actions$
         .pipe(
            filter((x) => !_.isNil(x.action.waitingContextCategoryFromDecorator)),
            filter((x) => x.status === ActionStatus.Dispatched)
         )
         .subscribe((x) => {
            const action = new LongRunningContextStarted(x.action.waitingContextCategoryFromDecorator, x.action.brickInstanceId);
            this.store.dispatch(action);
         });

      this.actions$
         .pipe(
            filter((x) => !_.isNil(x.action.waitingContextCategoryFromDecorator)),
            filter((x) => x.status === ActionStatus.Successful || x.status === ActionStatus.Canceled || x.status === ActionStatus.Errored)
         )
         .subscribe((x) => {
            this.store.dispatch(new LongRunningContextStopped(x.action.waitingContextCategoryFromDecorator, x.action.brickInstanceId));
         });
   }

   private static readonly NON_BRICK_KEY = 'NON_BRICK';

   @Selector([LongRunningContextState])
   public static isLongRunningContextInProgressForCategory(
      state: LongRunningContextStateModel
   ): (waitingContextCategory: LongRunningContextCategoryTyp, brickInstanceId?: string) => boolean {
      return (waitingContextCategory: LongRunningContextCategoryTyp, brickInstanceId: string = null) => {
         let waitingContexts: { [waitingContextKey: string]: number };

         if (_.isNil(brickInstanceId)) {
            brickInstanceId = LongRunningContextState.NON_BRICK_KEY;
         }
         const brickWaitingContexts = state.brickWaitingContexts[brickInstanceId];
         waitingContexts = _.isNil(brickWaitingContexts) ? {} : brickWaitingContexts.waitingContexts;

         const waitingCount = waitingContexts[waitingContextCategory.value];
         return waitingCount > 0;
      };
   }

   @Selector([LongRunningContextState.isLongRunningContextInProgressForCategory])
   public static isLongRunningContextInProgressForCategories(
      isLongRunningContextInProgressForCategoryFn
   ): (waitingContextCategories: LongRunningContextCategoryTyp[], brickInstanceId?: string) => boolean {
      return (waitingContextCategories: LongRunningContextCategoryTyp[], brickInstanceId: string = null) => {
         return _.some(waitingContextCategories, (category) => isLongRunningContextInProgressForCategoryFn(category, brickInstanceId));
      };
   }

   @Selector([LongRunningContextState])
   public static isAnyLongRunningContextInProgress(state: LongRunningContextStateModel): boolean {
      return _.some(state.brickWaitingContexts, (x) => _.some(x.waitingContexts, (y) => y > 0));
   }

   @Selector([LongRunningContextState])
   public static isAnyLongRunningContextInProgressForBrickId(state: LongRunningContextStateModel) {
      return (brickInstanceId: string) => {
         const waitingContainer = state.brickWaitingContexts[brickInstanceId];
         if (_.isNil(waitingContainer)) {
            return false;
         }
         return _.some(waitingContainer.waitingContexts, (y) => y > 0);
      };
   }

   @Action(LongRunningContextStarted)
   @ImmutableContext()
   public waitingContextActionStarted(ctx: StateContext<LongRunningContextStateModel>, action: LongRunningContextStarted) {
      ctx.setState((state) => {
         const brickInstanceId = _.isNil(action.brickInstanceId) ? LongRunningContextState.NON_BRICK_KEY : action.brickInstanceId;

         let brickWaitingContexts = state.brickWaitingContexts[brickInstanceId];
         if (_.isNil(brickWaitingContexts)) {
            brickWaitingContexts = {
               waitingContexts: {},
               brickInstanceId: brickInstanceId,
            };
         }
         const count = brickWaitingContexts.waitingContexts[action.waitingContextCategory.value];
         brickWaitingContexts.waitingContexts[action.waitingContextCategory.value] = _.isNil(count) ? 1 : count + 1;

         state.brickWaitingContexts[brickInstanceId] = brickWaitingContexts;
         return state;
      });
   }

   @Action(LongRunningContextStopped)
   @ImmutableContext()
   public waitingContextActionStopped(ctx: StateContext<LongRunningContextStateModel>, action: LongRunningContextStopped) {
      ctx.setState((state) => {
         const brickInstanceId = _.isNil(action.brickInstanceId) ? LongRunningContextState.NON_BRICK_KEY : action.brickInstanceId;

         let brickWaitingContexts = state.brickWaitingContexts[brickInstanceId];
         if (_.isNil(brickWaitingContexts)) {
            brickWaitingContexts = {
               waitingContexts: {},
               brickInstanceId: brickInstanceId,
            };
         }
         const count = brickWaitingContexts.waitingContexts[action.waitingContextCategory.value];
         brickWaitingContexts.waitingContexts[action.waitingContextCategory.value] = _.isNil(count) ? 0 : count - 1;

         state.brickWaitingContexts[brickInstanceId] = brickWaitingContexts;
         return state;
      });
   }
}
